import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Car, Loader2, AlertCircle, Search, X } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { obtenerVehiculos, Vehiculo, ObtenerVehiculosRequest } from "../../../../api/vehiculoService";
import Swal from "sweetalert2";
import VehicleDetailModal from "./VehicleDetailModal";

export default function VehiclesPage() {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";
  
  const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVehiculo, setSelectedVehiculo] = useState<Vehiculo | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [placaSearch, setPlacaSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasSearchedRef = useRef(false);

  const getImageUrl = (rutaFoto?: string) => {
    if (!rutaFoto || rutaFoto === "no_hay_imagen.png") {
      return null;
    }
    if (rutaFoto.startsWith('http://') || rutaFoto.startsWith('https://')) {
      return rutaFoto;
    }
    const apiBaseUrl = process.env.REACT_APP_API_PROD || 'https://api.easyph.com.co/api/';
    const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${rutaFoto}`;
  };

  const fetchVehiculos = useCallback(async (placa: string = "", showAlert: boolean = false) => {
    if (!unidadId) {
      setError("No se encontró el código de la unidad residencial.");
      return;
    }

    setIsLoading(true);
    setIsSearching(true);
    setError(null);

    try {
      const payload: ObtenerVehiculosRequest = {
        codigo: "",
        "unidad residencial codigo": unidadId,
        "apartamento codigo": "",
        "numero placa": placa.toUpperCase().trim(),
      };
      
      const response = await obtenerVehiculos(payload);
      
      if (response.code === "1" && response.object_) {
        setVehiculos(response.object_);
        hasSearchedRef.current = true;
        
        if (showAlert && response.object_.length === 0 && placa) {
          Swal.fire({
            icon: "info",
            title: "Sin resultados",
            text: `No se encontraron vehículos con la placa "${placa}".`,
            confirmButtonColor: "#1a3c34",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } else {
        setVehiculos([]);
      }
    } catch (err) {
      const errorMessage = "Error al cargar los vehículos: " + (err as Error).message;
      setError(errorMessage);
      if (showAlert) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonColor: "#1a3c34",
        });
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => setIsSearching(false), 300);
    }
  }, [unidadId]);

  useEffect(() => {
    if (unidadId) {
      fetchVehiculos("");
    }
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [unidadId, fetchVehiculos]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setPlacaSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchVehiculos(value);
    }, 500);
  };

  const handleClear = () => {
    setPlacaSearch("");
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    fetchVehiculos("");
  };

  const handleSelectVehiculo = (vehiculo: Vehiculo) => {
    setSelectedVehiculo(vehiculo);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedVehiculo(null);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900 w-full overflow-x-hidden">
      <Sidebar />
      
      <main className="flex-1 p-3 sm:p-4 md:p-6 md:ml-64 transition-all w-full max-w-full overflow-x-hidden">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 md:mb-6 text-military-600 break-words">
          Vehículos
        </h2>

        <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white w-full mb-4 sm:mb-6">
          <CardHeader className="p-3 sm:p-4 md:p-6">
            <CardTitle className="text-base sm:text-lg md:text-xl text-military-600 flex items-center gap-2">
              <Search className="h-4 w-4 sm:h-5 sm:w-5" />
              Buscar por Placa
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0 sm:pt-0 md:pt-0">
            <div className="relative">
              <div className="relative flex items-center">
                <Search className={`absolute left-3 h-5 w-5 text-gray-400 transition-colors ${isSearching ? 'text-military-500' : ''}`} />
                <Input
                  type="text"
                  value={placaSearch}
                  onChange={handleInputChange}
                  placeholder="Escribe para buscar por placa (ej: ABC123)..."
                  className="flex-1 pl-10 pr-10 text-sm sm:text-base transition-all duration-200"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 h-5 w-5 animate-spin text-military-500" />
                )}
                {placaSearch && !isSearching && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="absolute right-3 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Limpiar búsqueda"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white w-full">
          <CardHeader className="p-3 sm:p-4 md:p-6">
            <CardTitle className="text-base sm:text-lg md:text-xl text-military-600 flex items-center gap-2 w-full sm:w-auto">
              <Car className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="truncate">Lista de Vehículos</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0 sm:pt-0 md:pt-0">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-military-500" />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            ) : vehiculos.length === 0 && hasSearchedRef.current ? (
              <p className="text-gray-600 text-center py-8 text-sm sm:text-base">
                {placaSearch ? `No se encontraron vehículos con la placa "${placaSearch}".` : "No se encontraron vehículos registrados."}
              </p>
            ) : vehiculos.length === 0 ? (
              <p className="text-gray-600 text-center py-8 text-sm sm:text-base">
                {isSearching ? "Buscando..." : "Escribe para buscar vehículos por placa."}
              </p>
            ) : (
              <>
                <div className="block md:hidden space-y-3 sm:space-y-4">
                  {vehiculos.map((vehiculo) => {
                    const imageUrl = getImageUrl(vehiculo.veh_foto);
                    
                    return (
                      <div
                        key={vehiculo.veh_codigo}
                        onClick={() => handleSelectVehiculo(vehiculo)}
                        className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm hover:shadow-md transition cursor-pointer w-full"
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className="flex-shrink-0">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={vehiculo.veh_numero_placa}
                                className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover border-2 border-military-500"
                              />
                            ) : (
                              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-military-500">
                                <Car className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-base sm:text-lg text-gray-900 break-words">
                              Placa: {vehiculo.veh_numero_placa || "N/A"}
                            </h3>
                            <div className="mt-2 space-y-1 text-xs sm:text-sm text-gray-600">
                              {vehiculo.veh_tipo_nombre && (
                                <p>Tipo: {vehiculo.veh_tipo_nombre}</p>
                              )}
                              {vehiculo.veh_marca_nombre && (
                                <p>Marca: {vehiculo.veh_marca_nombre}</p>
                              )}
                              {vehiculo.veh_torre_nombre && vehiculo.veh_apartamento_nombre && (
                                <p>Torre {vehiculo.veh_torre_nombre} - Apt {vehiculo.veh_apartamento_nombre}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="hidden md:block overflow-x-auto w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Foto</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Placa</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Tipo</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Marca</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Ciudad</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Ubicación</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {vehiculos.map((vehiculo) => {
                              const imageUrl = getImageUrl(vehiculo.veh_foto);
                              
                              return (
                                <tr
                                  key={vehiculo.veh_codigo}
                                  className="hover:bg-gray-50 transition"
                                >
                                  <td className="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">
                                    {imageUrl ? (
                                      <img
                                        src={imageUrl}
                                        alt={vehiculo.veh_numero_placa}
                                        className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover border-2 border-military-500"
                                      />
                                    ) : (
                                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-military-500">
                                        <Car className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3">
                                    <div className="text-xs md:text-sm font-medium text-gray-900 break-words">
                                      {vehiculo.veh_numero_placa || "N/A"}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-gray-900 break-words">
                                    {vehiculo.veh_tipo_nombre || "No especificado"}
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-gray-900 break-words">
                                    {vehiculo.veh_marca_nombre || "No especificada"}
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-gray-900 break-words">
                                    {vehiculo.veh_nombre_ciudad || "N/A"}
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3">
                                    <div className="text-xs md:text-sm text-gray-900 break-words">
                                      {vehiculo.veh_torre_nombre || "N/A"}
                                    </div>
                                    <div className="text-xs md:text-sm text-gray-500 break-words">
                                      {vehiculo.veh_apartamento_nombre || ""}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm font-medium">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleSelectVehiculo(vehiculo)}
                                      className="border-military-500 text-military-500 hover:bg-military-100 text-xs md:text-sm px-2 md:px-3 py-1 md:py-2"
                                    >
                                      Ver Detalle
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {selectedVehiculo && (
          <VehicleDetailModal
            isOpen={showDetailModal}
            vehiculo={selectedVehiculo}
            onClose={handleCloseModal}
          />
        )}
      </main>
    </div>
  );
}

